import { cn } from '@foundation/utils';
import {
  AntiagingIcon,
  CalmingIcon,
  DermatologicallyTestedIcon,
  EasyAndConvenientIcon,
  FirmingIcon,
  GlutenFreeIcon,
  HalalIcon,
  HydratingIcon,
  KosherIcon,
  LactoseFreeIcon,
  PreservativeFreeIcon,
  ProtectingIcon,
  RefreshingIcon,
  RegeneratingIcon,
  SugarFreeIcon,
  VeganIcon,
  VegetarianIcon,
  WithNaturalFlavorsIcon,
  WithNaturalSweetenerIcon,
} from '@shared/ui/icons';

const SupportedTags = [
  {
    tag: 'pim:feature:vegan',
    icon: VeganIcon,
  },
  {
    tag: 'pim:feature:vegetarian',
    icon: VegetarianIcon,
  },
  {
    tag: 'pim:feature:glutenfree',
    icon: GlutenFreeIcon,
  },
  {
    tag: 'pim:feature:preservativefree',
    icon: PreservativeFreeIcon,
  },
  {
    tag: 'pim:feature:dermatologicallytested',
    icon: DermatologicallyTestedIcon,
  },
  {
    tag: 'pim:feature:hydrating',
    icon: HydratingIcon,
  },
  {
    tag: 'pim:feature:antiaging',
    icon: AntiagingIcon,
  },
  {
    tag: 'pim:feature:protecting',
    icon: ProtectingIcon,
  },
  {
    tag: 'pim:feature:firming',
    icon: FirmingIcon,
  },
  {
    tag: 'pim:feature:regenerating',
    icon: RegeneratingIcon,
    label: 'Regenerating',
  },
  {
    tag: 'pim:feature:calming',
    icon: CalmingIcon,
  },
  {
    tag: 'pim:feature:lactosefree',
    icon: LactoseFreeIcon,
  },
  {
    tag: 'pim:feature:halal',
    icon: HalalIcon,
  },
  {
    tag: 'pim:feature:sugarfree',
    icon: SugarFreeIcon,
  },
  {
    tag: 'pim:feature:kosher',
    icon: KosherIcon,
  },
  {
    tag: 'pim:feature:withnaturalsweetner',
    icon: WithNaturalSweetenerIcon,
  },
  {
    tag: 'pim:feature:refreshing',
    icon: RefreshingIcon,
  },
  {
    tag: 'pim:feature:easyandconvenient',
    icon: EasyAndConvenientIcon,
  },
  {
    tag: 'pim:feature:withnaturalflavors',
    icon: WithNaturalFlavorsIcon,
  },
];

const SupportedTagsMap = new Map(SupportedTags.map((tag) => [tag.tag, tag]));

interface IProductVariantsProps {
  localizedTags?: ReadonlyArray<{
    readonly tag: string;
    readonly localization: string | null;
  }>;
}

export function ProductFeatures({ localizedTags }: IProductVariantsProps) {
  if (!localizedTags) {
    return null;
  }

  const featuresTags = localizedTags
    .filter((tag) => SupportedTagsMap.has(tag.tag.toLowerCase().trim()))
    // Only show tags with localization in production builds
    // to avoid showing tags without localization to end users
    .filter((tag) => (import.meta.env.PROD ? !!tag.localization : true));

  if (featuresTags.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-5')}>
      <hr className="border-primary" />
      <ul className="flex gap-6 overflow-x-auto scrollbar-hidden">
        {featuresTags.map((tag) => {
          const tagDetails = SupportedTags.find(
            (supportedTag) => supportedTag.tag === tag.tag,
          );
          if (!tagDetails || !tagDetails.icon) {
            return null;
          }
          const localizedLabel = tag.localization ?? tagDetails.label ?? '';
          const IconComponent = tagDetails.icon;

          return (
            <li
              className="flex flex-row items-center gap-1 shrink-0"
              key={tag.tag}
            >
              <IconComponent className="w-6 h-6 md:w-8 md:h-8" />
              <span className="text-center">{localizedLabel}</span>
            </li>
          );
        })}
      </ul>
      <hr className="border-primary" />
    </div>
  );
}
